/**
 * useCompanyLogo Hook
 *
 * Gerencia upload, delete e URL de logos de empresas no Supabase Storage
 */

import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export interface CompanyLogoUploadOptions {
  companyId: string;
  file: File;
  onSuccess?: (url: string) => void;
  onError?: (error: Error) => void;
}

export function useCompanyLogo() {
  const [isUploading, setIsUploading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  /**
   * Upload logo da empresa para o Supabase Storage
   */
  const uploadLogo = async ({ companyId, file, onSuccess, onError }: CompanyLogoUploadOptions) => {
    setIsUploading(true);

    try {
      // Validate file
      if (!file) {
        throw new Error('Nenhum arquivo selecionado');
      }

      // Validate file size (max 5MB)
      const maxSize = 5 * 1024 * 1024; // 5MB
      if (file.size > maxSize) {
        throw new Error('Arquivo muito grande. Tamanho máximo: 5MB');
      }

      // Validate file type
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/svg+xml'];
      if (!allowedTypes.includes(file.type)) {
        throw new Error('Tipo de arquivo não permitido. Use: JPG, PNG, WEBP ou SVG');
      }

      // Generate unique filename with timestamp
      const fileExt = file.name.split('.').pop();
      const fileName = `${companyId}-${Date.now()}.${fileExt}`;
      const filePath = `company-logos/${fileName}`;

      // Upload to Supabase Storage
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('company-logos')
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: false,
        });

      if (uploadError) {
        throw uploadError;
      }

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('company-logos')
        .getPublicUrl(filePath);

      if (!publicUrl) {
        throw new Error('Erro ao obter URL pública do logo');
      }

      // Update company record with new logo URL
      const { error: updateError } = await supabase
        .from('companies')
        .update({ logo_url: publicUrl })
        .eq('id', companyId);

      if (updateError) {
        // Rollback: delete uploaded file
        await supabase.storage
          .from('company-logos')
          .remove([filePath]);

        throw updateError;
      }

      toast.success('Logo enviado com sucesso!');
      onSuccess?.(publicUrl);

      return publicUrl;
    } catch (error: any) {
      console.error('Erro ao fazer upload do logo:', error);
      const errorMessage = error.message || 'Erro ao fazer upload do logo';
      toast.error(errorMessage);
      onError?.(error);
      throw error;
    } finally {
      setIsUploading(false);
    }
  };

  /**
   * Delete logo da empresa do Supabase Storage
   */
  const deleteLogo = async (companyId: string, logoUrl: string) => {
    setIsDeleting(true);

    try {
      // Extract file path from URL
      const urlParts = logoUrl.split('/');
      const fileName = urlParts[urlParts.length - 1];
      const filePath = `company-logos/${fileName}`;

      // Delete from storage
      const { error: deleteError } = await supabase.storage
        .from('company-logos')
        .remove([filePath]);

      if (deleteError) {
        throw deleteError;
      }

      // Update company record to remove logo URL
      const { error: updateError } = await supabase
        .from('companies')
        .update({ logo_url: null })
        .eq('id', companyId);

      if (updateError) {
        throw updateError;
      }

      toast.success('Logo removido com sucesso!');

      return true;
    } catch (error: any) {
      console.error('Erro ao remover logo:', error);
      toast.error(error.message || 'Erro ao remover logo');
      throw error;
    } finally {
      setIsDeleting(false);
    }
  };

  return {
    uploadLogo,
    deleteLogo,
    isUploading,
    isDeleting,
  };
}
